/**
 * AdminBookingsPage — replaces the AdminComingSoon stub at /admin/bookings
 * (Backlog E.4).
 *
 * Owns ALL state for the admin bookings calendar:
 *   - Filter bar: status (multi-checkbox CSV), provider (multi-select FE-filter
 *     per D3), zip (free-text 5-digit, debounced 300ms per D5)
 *   - View mode (day | week | month) + date cursor → derives (start, end)
 *   - Bookings fetch via `GET /api/admin/bookings` (vanilla useEffect + axios,
 *     mirrors AdminDashboardPage.jsx pattern per Q2(a))
 *   - Span-cap clamp + advisory banner (defensive — D6)
 *   - Booking detail modal on event click (read-only per Q1(a))
 *   - Mobile <Sheet> drawer (triggered by SlidersHorizontal icon per Q4(a))
 *
 * What it does NOT do:
 *   - Mutate bookings (admin mutations live in Backlog D confirm-gated flows;
 *     the detail modal here is strictly read-only)
 *   - Fan out N requests for multi-select provider (D3 — single request +
 *     FE-side filter)
 */
import { useEffect, useMemo, useState } from "react";

import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import addDays from "date-fns/addDays";
import differenceInDays from "date-fns/differenceInDays";
import format from "date-fns/format";

import {
  SlidersHorizontal,
  Loader2,
  AlertCircle,
  Info,
  Check,
  X,
} from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from "@/components/ui/sheet";
import AdminCalendarView from "@/components/admin/AdminCalendarView";
import BookingDetailModal from "@/components/admin/BookingDetailModal";


const STATUS_OPTIONS = [
  { value: "payment_pending", label: "Payment pending" },
  { value: "accepted", label: "Accepted" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const MODES = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

const MAX_SPAN_DAYS = 92;
const DEBOUNCE_MS = 300;


// ---------------------------------------------------------------------------
// rangeForView — exported for unit tests (FT9 span-cap)
// ---------------------------------------------------------------------------
export function rangeForView(mode, anchorDate) {
  const anchor = anchorDate instanceof Date ? anchorDate : new Date(anchorDate);
  let start;
  let end;
  if (mode === "day") {
    start = anchor;
    end = anchor;
  } else if (mode === "week") {
    start = startOfWeek(anchor, { weekStartsOn: 0 });
    end = endOfWeek(anchor, { weekStartsOn: 0 });
  } else {
    // month
    const ms = startOfMonth(anchor);
    const me = endOfMonth(anchor);
    start = startOfWeek(ms, { weekStartsOn: 0 });
    end = endOfWeek(me, { weekStartsOn: 0 });
  }
  // Defensive 92-day safety clamp — v1 paths never naturally exceed this.
  const days = differenceInDays(end, start);
  const clamped = days > MAX_SPAN_DAYS;
  if (clamped) end = addDays(start, MAX_SPAN_DAYS);
  return {
    start: format(start, "yyyy-MM-dd"),
    end: format(end, "yyyy-MM-dd"),
    clamped,
  };
}


function dedupProviders(items) {
  const seen = new Map();
  for (const b of items || []) {
    if (b.provider_id && !seen.has(b.provider_id)) {
      seen.set(b.provider_id, {
        id: b.provider_id,
        business_name: b.provider_business_name || b.provider_id,
      });
    }
  }
  return Array.from(seen.values());
}


// ---------------------------------------------------------------------------
// FilterBar — desktop top bar
// ---------------------------------------------------------------------------
function FilterBar({
  statuses, setStatuses,
  providersInView, pickedProviderIds, setPickedProviderIds,
  zipInput, setZipInput,
  mode, setMode,
  onOpenMobileSheet,
  totalCount, visibleCount,
}) {
  const statusLabel =
    statuses.length === 0
      ? "All statuses"
      : `${statuses.length} status${statuses.length === 1 ? "" : "es"}`;
  const providerLabel =
    pickedProviderIds.size === 0
      ? "All providers"
      : `${pickedProviderIds.size} provider${pickedProviderIds.size === 1 ? "" : "s"}`;

  function toggleStatus(value) {
    setStatuses((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
  }

  function toggleProvider(id) {
    setPickedProviderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div
      data-testid="admin-bookings-filter-bar"
      className="flex flex-wrap items-center gap-2 rounded-2xl border border-pp-border bg-pp-surface p-3"
    >
      {/* Status filter ----------------------------------------------------- */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            data-testid="filter-status-trigger"
            className="rounded-full"
          >
            {statusLabel}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel>Filter by status</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {STATUS_OPTIONS.map((opt) => (
            <DropdownMenuCheckboxItem
              key={opt.value}
              checked={statuses.includes(opt.value)}
              onCheckedChange={() => toggleStatus(opt.value)}
              onSelect={(e) => e.preventDefault()}
              data-testid={`filter-status-option-${opt.value}`}
            >
              {opt.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Provider filter --------------------------------------------------- */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            data-testid="filter-provider-trigger"
            className="rounded-full"
            disabled={providersInView.length === 0}
          >
            {providerLabel}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel>Filter by provider</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {providersInView.length === 0 && (
            <div className="px-2 py-1 text-xs text-pp-text-muted">
              (None in current view)
            </div>
          )}
          {providersInView.map((p) => (
            <DropdownMenuCheckboxItem
              key={p.id}
              checked={pickedProviderIds.has(p.id)}
              onCheckedChange={() => toggleProvider(p.id)}
              onSelect={(e) => e.preventDefault()}
              data-testid={`filter-provider-option-${p.id}`}
            >
              {p.business_name}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Zip input --------------------------------------------------------- */}
      <div className="relative">
        <Input
          value={zipInput}
          onChange={(e) => setZipInput(e.target.value.replace(/\D/g, "").slice(0, 5))}
          placeholder="ZIP"
          inputMode="numeric"
          maxLength={5}
          data-testid="filter-zip-input"
          className="h-9 w-24 rounded-full text-center"
        />
        {zipInput.length > 0 && zipInput.length < 5 && (
          <span
            data-testid="filter-zip-hint"
            className="absolute -bottom-4 left-0 text-[10px] text-pp-text-muted"
          >
            Enter 5 digits
          </span>
        )}
      </div>

      <div className="ml-auto flex items-center gap-2">
        {/* Mobile sheet trigger (Q4(a)) ----------------------------------- */}
        <Button
          variant="outline"
          size="sm"
          onClick={onOpenMobileSheet}
          data-testid="mobile-filter-trigger"
          className="rounded-full md:hidden"
          aria-label="Open filters"
        >
          <SlidersHorizontal className="h-4 w-4" />
        </Button>

        {/* View toggle ----------------------------------------------------- */}
        <div
          role="tablist"
          className="inline-flex overflow-hidden rounded-full border border-pp-border bg-pp-surface"
          data-testid="view-toggle"
        >
          {MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              role="tab"
              aria-selected={mode === m.value}
              onClick={() => setMode(m.value)}
              data-testid={`view-toggle-${m.value}`}
              className={
                "px-3 py-1.5 text-xs font-semibold transition-colors " +
                (mode === m.value
                  ? "bg-[#F89623] text-white"
                  : "text-pp-text hover:bg-pp-surface-2")
              }
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* FE-filter counter — only when provider multi-select narrowing ---- */}
      {pickedProviderIds.size > 0 && (
        <div
          className="basis-full text-xs text-pp-text-muted"
          data-testid="bookings-fe-filter-count"
        >
          Showing {visibleCount} of {totalCount} bookings
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// SpanCapBanner
// ---------------------------------------------------------------------------
function SpanCapBanner() {
  return (
    <div
      data-testid="span-cap-banner"
      className="flex items-center gap-2 rounded-2xl border border-pp-border bg-pp-surface-2 px-3 py-2 text-xs text-pp-text-muted"
    >
      <Info className="h-3.5 w-3.5" />
      <span>Showing the first 92 days of the selected range.</span>
    </div>
  );
}


// ---------------------------------------------------------------------------
// AdminBookingsPage (state owner)
// ---------------------------------------------------------------------------
export default function AdminBookingsPage() {
  const [mode, setMode] = useState("month");
  const [date, setDate] = useState(() => new Date());     // Q3(a)
  const [statuses, setStatuses] = useState([]);
  const [pickedProviderIds, setPickedProviderIds] = useState(new Set());
  const [zipInput, setZipInput] = useState("");
  const [debouncedZip, setDebouncedZip] = useState("");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [detailBooking, setDetailBooking] = useState(null);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Forced day-mode on mobile (mirrors ProviderSchedulePage:284,305 pattern).
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined"
      && window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  const effectiveMode = isMobile ? "day" : mode;

  // Derived (start, end) for fetch -----------------------------------------
  const { start, end, clamped } = useMemo(
    () => rangeForView(effectiveMode, date),
    [effectiveMode, date]
  );

  // Debounce zip (D5) -------------------------------------------------------
  useEffect(() => {
    const t = setTimeout(() => setDebouncedZip(zipInput.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [zipInput]);

  // Fetch effect (Q2(a) — vanilla useEffect + api.get) ----------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = { start, end, page_size: 500 };
    if (statuses.length) {
      params.status = [...statuses].sort().join(",");
    }
    if (debouncedZip && /^\d{5}$/.test(debouncedZip)) {
      params.zip = debouncedZip;
    }
    // D3: NO provider_id param. Multi-select handled FE-side.
    api.get("/admin/bookings", { params })
      .then((res) => {
        if (cancelled) return;
        setItems(res.data?.items || []);
      })
      .catch((e) => {
        if (cancelled) return;
        const detail = e?.response?.data?.detail;
        setError(formatApiErrorDetail(detail) || "Failed to load bookings.");
        setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [start, end, statuses, debouncedZip]);

  // Visible providers + FE-filter pass --------------------------------------
  const visibleProviders = useMemo(() => dedupProviders(items), [items]);
  const visibleItems = useMemo(() => {
    if (pickedProviderIds.size === 0) return items;
    return items.filter((b) => pickedProviderIds.has(b.provider_id));
  }, [items, pickedProviderIds]);

  return (
    <div className="space-y-4" data-testid="admin-bookings-page">
      <header className="flex items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-pp-text">Bookings</h1>
          <p className="text-sm text-pp-text-muted">
            Cross-platform admin calendar — every provider, every status.
          </p>
        </div>
      </header>

      <FilterBar
        statuses={statuses}
        setStatuses={setStatuses}
        providersInView={visibleProviders}
        pickedProviderIds={pickedProviderIds}
        setPickedProviderIds={setPickedProviderIds}
        zipInput={zipInput}
        setZipInput={setZipInput}
        mode={effectiveMode}
        setMode={setMode}
        onOpenMobileSheet={() => setMobileFilterOpen(true)}
        totalCount={items.length}
        visibleCount={visibleItems.length}
      />

      {/* Mobile filter sheet ------------------------------------------------ */}
      <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
        <SheetContent side="bottom" data-testid="mobile-filter-sheet">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-pp-text-muted">
                Status
              </div>
              <div className="grid grid-cols-2 gap-2">
                {STATUS_OPTIONS.map((opt) => {
                  const checked = statuses.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setStatuses((prev) =>
                        checked ? prev.filter((s) => s !== opt.value) : [...prev, opt.value]
                      )}
                      data-testid={`mobile-filter-status-option-${opt.value}`}
                      className={
                        "flex items-center justify-between rounded-full border px-3 py-1.5 text-xs font-medium " +
                        (checked
                          ? "border-[#F89623] bg-[#F89623]/10 text-pp-text"
                          : "border-pp-border bg-pp-surface text-pp-text-muted")
                      }
                    >
                      <span>{opt.label}</span>
                      {checked && <Check className="h-3.5 w-3.5" />}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-pp-text-muted">
                Zip
              </div>
              <Input
                value={zipInput}
                onChange={(e) => setZipInput(e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="5-digit ZIP"
                inputMode="numeric"
                maxLength={5}
                data-testid="mobile-filter-zip-input"
              />
            </div>
          </div>
          <SheetFooter>
            <SheetClose asChild>
              <Button variant="outline" data-testid="mobile-filter-close">
                Done
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {clamped && <SpanCapBanner />}

      {error && (
        <div
          data-testid="admin-bookings-error"
          className="flex items-center gap-2 rounded-2xl border border-pp-border bg-pp-surface px-3 py-2 text-sm text-pp-text"
        >
          <AlertCircle className="h-4 w-4 text-red-500" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div
          data-testid="admin-bookings-skeleton"
          className="flex h-[480px] items-center justify-center rounded-3xl border border-pp-border bg-pp-surface"
        >
          <Loader2 className="h-6 w-6 animate-spin text-pp-text-muted" />
        </div>
      ) : (
        <Card
          className="rounded-3xl border-pp-border bg-pp-surface p-4 shadow-none"
          data-testid="admin-bookings-calendar-card"
        >
          {visibleItems.length === 0 ? (
            <div
              data-testid="admin-bookings-empty"
              className="flex h-[420px] items-center justify-center text-sm text-pp-text-muted"
            >
              No bookings match your current filters.
            </div>
          ) : (
            <AdminCalendarView
              events={visibleItems}
              mode={effectiveMode}
              onModeChange={setMode}
              date={date}
              onNavigate={setDate}
              onSelectEvent={setDetailBooking}
              providers={visibleProviders}
            />
          )}
        </Card>
      )}

      <BookingDetailModal
        booking={detailBooking}
        onClose={() => setDetailBooking(null)}
      />
    </div>
  );
}
